import React, { useEffect, useMemo, useState } from "react";
import {
  getPutBotSettings,
  savePutBotSettings,
  getPutBotStatus,
  getPutBotLogs,
  scanPutBot,
  runPutBotTick,
  managePutBotExits,
} from "../api/botApi";

const DEFAULT_SETTINGS = {
  enabled: 0,
  paper_mode: 1,
  allow_live: 0,
  max_positions: 3,
  max_contracts_per_trade: 1,
  min_put_score: 72,
  min_dte: 7,
  max_dte: 45,
  target_delta_abs: 0.4,
  min_open_interest: 100,
  min_volume: 25,
  max_spread_pct: 30,
  profit_target_pct: 35,
  stop_loss_pct: -30,
  trail_giveback_pct: 18,
  cooldown_minutes: 60,
  market_filter: 1,
  news_sentiment: 1,
  avoid_0dte: 1,
  max_daily_new_trades: 3,
  watchlist: [],
};

function asBoolInt(v) {
  return v ? 1 : 0;
}

function Pill({ children, tone = "neutral" }) {
  const cls =
    tone === "good"
      ? "border-green-500/40 text-green-300 bg-green-500/10"
      : tone === "bad"
      ? "border-red-500/40 text-red-300 bg-red-500/10"
      : tone === "gold"
      ? "border-yellow-500/40 text-yellow-300 bg-yellow-500/10"
      : "border-white/15 text-gray-300 bg-white/5";
  return <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs ${cls}`}>{children}</span>;
}

function Toggle({ label, checked, onChange, danger = false }) {
  return (
    <label className="flex items-center justify-between gap-3 rounded-xl border border-white/10 bg-black/25 px-3 py-2">
      <span className={danger ? "text-red-200" : "text-gray-200"}>{label}</span>
      <button
        type="button"
        onClick={() => onChange(!checked)}
        className={`relative h-7 w-12 rounded-full border transition ${
          checked ? (danger ? "bg-red-500/60 border-red-300/50" : "bg-yellow-500/60 border-yellow-300/50") : "bg-gray-800 border-white/20"
        }`}
      >
        <span
          className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition ${checked ? "left-6" : "left-0.5"}`}
        />
      </button>
    </label>
  );
}

function NumberInput({ label, value, onChange, step = 1, min, max }) {
  return (
    <label className="block">
      <div className="mb-1 text-xs uppercase tracking-wide text-gray-400">{label}</div>
      <input
        type="number"
        step={step}
        min={min}
        max={max}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
        className="w-full rounded-xl border border-white/10 bg-black/40 px-3 py-2 text-gray-100 outline-none focus:border-yellow-400/60"
      />
    </label>
  );
}

function parseWatchlistText(text) {
    return Array.from(
        new Set(
            String(text || "")
                .split(/[\s,\n\r\t,]+/)
                .map((s) => s.trim().toUpperCase())
                .filter(Boolean)
        )
    );
}

function watchlistToText(watchlist) {
    return (watchlist || []).join(", ");
}


export default function PutBotPanel() {
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [status, setStatus] = useState(null);
  const [logs, setLogs] = useState([]);
  const [scan, setScan] = useState([]);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  const [watchlistText, setWatchlistText] = useState("");

  async function refreshAll() {
    setLoading(true);
    setMessage("");

    try {
      const [s, st, l] = await Promise.all([
        getPutBotSettings(),
        getPutBotStatus(),
        getPutBotLogs(50),
      ]);

      const nextSettings = {
        ...DEFAULT_SETTINGS,
        ...(s?.settings || s || {}),
      };

      setSettings(nextSettings);
      setWatchlistText(watchlistToText(nextSettings?.watchlist || []));

      if (st) setStatus(st);
      if (l?.logs) setLogs(l.logs);
    } catch (err) {
      setMessage(`PUT Bot refresh failed: ${err.message || err}`);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    refreshAll();
  }, []);

  async function save(next = settings) {
    setLoading(true);
    setMessage("");
    try {
      const payload = {
        ...next,
        watchlist: parseWatchlistText(watchlistText),
      };
      const saved = await savePutBotSettings(payload);
      const nextSettings = { ...DEFAULT_SETTINGS, ...(saved?.settings || payload) };
      setSettings(nextSettings);
      setWatchlistText(watchlistToText(nextSettings?.watchlist || payload.watchlist || []));
      setMessage("PUT Bot settings saved.");
      await refreshAll();
    } catch (err) {
      setMessage(`Save failed: ${err.message || err}`);
    } finally {
      setLoading(false);
    }
  }

  function update(key, value) {
    setSettings((prev) => ({ ...prev, [key]: value }));
  }

  async function savePutWatchlist(list) {
    setLoading(true);
    setMessage("");

    try {
      const cleaned = Array.isArray(list)
        ? list
        : parseWatchlistText(watchlistText);

      const payload = {
        ...settings,
        watchlist: cleaned,
      };

      const saved = await savePutBotSettings(payload);

      const nextSettings = {
        ...DEFAULT_SETTINGS,
        ...(saved?.settings || payload),
      };

      setSettings(nextSettings);
      setWatchlistText(watchlistToText(nextSettings?.watchlist || cleaned));

      const freshStatus = await getPutBotStatus();
      setStatus(freshStatus);

      const freshLogs = await getPutBotLogs(100);
      setLogs(freshLogs.logs || []);

      setResult({
        ok: true,
        message: `Saved ${cleaned.length} PUT Bot watchlist tickers.`,
        watchlist: cleaned,
      });

      setMessage(`Saved ${cleaned.length} PUT Bot watchlist tickers.`);
    } catch (err) {
      setMessage(`Save PUT watchlist failed: ${err.message || err}`);
    } finally {
      setLoading(false);
    }
  }

  async function doScan() {
    setLoading(true);
    setMessage("");
    try {
      await save(settings);
      const data = await scanPutBot(12);
      setScan(data?.candidates || []);
      setResult(data);
    } catch (err) {
      setMessage(`Scan failed: ${err.message || err}`);
    } finally {
      setLoading(false);
    }
  }

  async function runSimulate() {
    setLoading(true);
    setMessage("");
    try {
      await save(settings);
      const data = await runPutBotTick({ execute: false });
      setResult(data);
      setMessage(data?.action ? `Simulation complete: ${data.action}` : "Simulation complete.");
      await refreshAll();
    } catch (err) {
      setMessage(`Simulation failed: ${err.message || err}`);
    } finally {
      setLoading(false);
    }
  }

  async function runLiveOrPaper() {
    setLoading(true);
    setMessage("");
    try {
      await save(settings);
      const data = await runPutBotTick({ execute: true });
      setResult(data);
      setMessage(data?.ok === false ? `PUT Bot run failed: ${data.error || "unknown"}` : "PUT Bot tick executed.");
      await refreshAll();
    } catch (err) {
      setMessage(`Run failed: ${err.message || err}`);
    } finally {
      setLoading(false);
    }
  }

  async function simulateExits() {
    setLoading(true);
    setMessage("");
    try {
      const data = await managePutBotExits({ execute: false });
      setResult(data);
      setMessage("PUT exit simulation complete.");
      await refreshAll();
    } catch (err) {
      setMessage(`Exit simulation failed: ${err.message || err}`);
    } finally {
      setLoading(false);
    }
  }

  async function executeExits() {
    setLoading(true);
    setMessage("");
    try {
      const data = await managePutBotExits({ execute: true });
      setResult(data);
      setMessage(data?.ok === false ? `PUT exits failed: ${data.error || "unknown"}` : "PUT exits executed.");
      await refreshAll();
    } catch (err) {
      setMessage(`Exit execution failed: ${err.message || err}`);
    } finally {
      setLoading(false);
    }
  }

  const liveArmed = Number(settings.paper_mode) === 0 && Number(settings.allow_live) === 1;

  return (
    <section className="rounded-2xl border border-yellow-500/20 bg-gradient-to-b from-zinc-950 to-black p-4 shadow-xl shadow-yellow-900/10">
      <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-xl font-bold text-yellow-300">PUT Bot / Bearish Options Bot</h2>
            <Pill tone={settings.enabled ? "gold" : "neutral"}>{settings.enabled ? "Enabled" : "Disabled"}</Pill>
            <Pill tone={settings.paper_mode ? "good" : liveArmed ? "bad" : "neutral"}>
              {settings.paper_mode ? "Paper Mode" : liveArmed ? "Live Armed" : "Live Blocked"}
            </Pill>
            {status?.market_open ? <Pill tone="good">Market Open</Pill> : <Pill>Market Closed</Pill>}
          </div>
          <p className="mt-1 text-sm text-gray-400">
            Buys PUT contracts only. Exits with profit protection, stop loss, bearish-thesis checks, DTE checks, and sentiment flips.
          </p>
        </div>
        <button
          type="button"
          onClick={refreshAll}
          disabled={loading}
          className="rounded-xl border border-white/10 bg-white/5 px-4 py-2 text-sm text-gray-100 hover:bg-white/10 disabled:opacity-50"
        >
          Refresh
        </button>
      </div>

      {message && (
        <div className="mb-4 rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-sm text-gray-200">{message}</div>
      )}

      <div className="grid gap-3 md:grid-cols-3">
        <Toggle label="Enable PUT Bot" checked={!!settings.enabled} onChange={(v) => update("enabled", asBoolInt(v))} />
        <Toggle label="Paper Mode" checked={!!settings.paper_mode} onChange={(v) => update("paper_mode", asBoolInt(v))} />
        <Toggle label="Allow Live Trading" checked={!!settings.allow_live} onChange={(v) => update("allow_live", asBoolInt(v))} danger />
        <Toggle label="Use SPY/QQQ Market Filter" checked={!!settings.market_filter} onChange={(v) => update("market_filter", asBoolInt(v))} />
        <Toggle label="Use News Sentiment" checked={!!settings.news_sentiment} onChange={(v) => update("news_sentiment", asBoolInt(v))} />
        <Toggle label="Avoid 0DTE" checked={!!settings.avoid_0dte} onChange={(v) => update("avoid_0dte", asBoolInt(v))} />
      </div>

      <div className="mt-4 grid gap-3 md:grid-cols-4">
        <NumberInput label="Minimum PUT Score" value={settings.min_put_score} onChange={(v) => update("min_put_score", v)} min={1} max={100} />
        <NumberInput label="Max Positions" value={settings.max_positions} onChange={(v) => update("max_positions", v)} min={1} max={20} />
        <NumberInput label="Contracts / Trade" value={settings.max_contracts_per_trade} onChange={(v) => update("max_contracts_per_trade", v)} min={1} max={10} />
        <NumberInput label="Max New Trades / Day" value={settings.max_daily_new_trades} onChange={(v) => update("max_daily_new_trades", v)} min={1} max={20} />
        <NumberInput label="Min DTE" value={settings.min_dte} onChange={(v) => update("min_dte", v)} min={1} max={120} />
        <NumberInput label="Max DTE" value={settings.max_dte} onChange={(v) => update("max_dte", v)} min={1} max={365} />
        <NumberInput label="Target Delta Abs" value={settings.target_delta_abs} onChange={(v) => update("target_delta_abs", v)} step={0.05} min={0.1} max={0.9} />
        <NumberInput label="Cooldown Minutes" value={settings.cooldown_minutes} onChange={(v) => update("cooldown_minutes", v)} min={0} max={1440} />
        <NumberInput label="Min Open Interest" value={settings.min_open_interest} onChange={(v) => update("min_open_interest", v)} min={0} />
        <NumberInput label="Min Volume" value={settings.min_volume} onChange={(v) => update("min_volume", v)} min={0} />
        <NumberInput label="Max Spread %" value={settings.max_spread_pct} onChange={(v) => update("max_spread_pct", v)} step={1} min={1} max={100} />
        <NumberInput label="Profit Target %" value={settings.profit_target_pct} onChange={(v) => update("profit_target_pct", v)} step={1} min={1} max={500} />
        <NumberInput label="Stop Loss %" value={settings.stop_loss_pct} onChange={(v) => update("stop_loss_pct", v)} step={1} min={-100} max={0} />
        <NumberInput label="Trail Giveback %" value={settings.trail_giveback_pct} onChange={(v) => update("trail_giveback_pct", v)} step={1} min={1} max={100} />
      </div>

      <div className="mt-4 bg-gray-950 border border-gray-800 rounded-xl p-4">
          <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
              <div>
                  <div className="text-sm font-semibold">PUT Bot Watchlist</div>
                  <div className="text-xs text-gray-400">
                      Used when Scan All is OFF. Paste commas, spaces, or one ticker per line.
                  </div>
              </div>
              <span className="px-3 py-1 rounded-full text-xs bg-gray-800 border border-gray-700">
                  {parseWatchlistText(watchlistText).length} tickers
              </span>
          </div>

          <textarea
              value={watchlistText}
              onChange={(e) => setWatchlistText(e.target.value)}
              disabled={loading}
              rows={6}
              placeholder="TSLA, NVDA, AMD, META, NFLX, QQQ, SPY"
              className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-sm text-gray-200"
          />

          <div className="mt-3 flex gap-2 flex-wrap">
              <button
                  disabled={loading}
                  onClick={() => savePutWatchlist(parseWatchlistText(watchlistText))}
                  className="px-3 py-2 rounded-lg bg-blue-900/30 border border-blue-700 text-sm"
              >
                  Save PUT Watchlist
              </button>

              <button
                  disabled={loading}
                  onClick={() => {
                      const preset = [
                          "AAPL", "MSFT", "NVDA", "AMZN", "GOOGL", "META", "TSLA", "AMD",
                          "PLTR", "AVGO", "NFLX", "SPY", "QQQ", "IWM", "SOXL", "JPM",
                          "XOM", "WMT", "BAC", "CRM"
                      ];
                      setWatchlistText(preset.join(", "));
                      savePutWatchlist(preset);
                  }}
                  className="px-3 py-2 rounded-lg bg-gray-800 border border-gray-700 text-sm"
              >
                  Use Liquid PUT Preset
              </button>

              <button
                  disabled={loading}
                  onClick={() => {
                      setWatchlistText("");
                      savePutWatchlist([]);
                  }}
                  className="px-3 py-2 rounded-lg bg-red-900/30 border border-red-700 text-sm"
              >
                  Clear PUT Watchlist
              </button>
          </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        <button onClick={() => save()} disabled={loading} className="rounded-xl bg-yellow-500 px-4 py-2 font-semibold text-black hover:bg-yellow-400 disabled:opacity-50">
          Save PUT Settings
        </button>
        <button onClick={doScan} disabled={loading} className="rounded-xl border border-yellow-500/30 bg-yellow-500/10 px-4 py-2 text-yellow-200 hover:bg-yellow-500/20 disabled:opacity-50">
          Scan PUT Candidates
        </button>
        <button onClick={runSimulate} disabled={loading} className="rounded-xl border border-white/10 bg-white/5 px-4 py-2 text-gray-100 hover:bg-white/10 disabled:opacity-50">
          Simulate PUT Tick
        </button>
        <button onClick={runLiveOrPaper} disabled={loading} className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-2 text-red-200 hover:bg-red-500/20 disabled:opacity-50">
          Execute PUT Tick
        </button>
        <button onClick={simulateExits} disabled={loading} className="rounded-xl border border-white/10 bg-white/5 px-4 py-2 text-gray-100 hover:bg-white/10 disabled:opacity-50">
          Simulate PUT Exits
        </button>
        <button onClick={executeExits} disabled={loading} className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-2 text-red-200 hover:bg-red-500/20 disabled:opacity-50">
          Execute PUT Exits
        </button>
      </div>

      {status?.open_trades?.length > 0 && (
        <div className="mt-5">
          <h3 className="mb-2 font-semibold text-gray-100">Open PUT Bot Trades</h3>
          <div className="overflow-x-auto rounded-xl border border-white/10">
            <table className="min-w-full text-sm">
              <thead className="bg-white/5 text-gray-400">
                <tr>
                  <th className="px-3 py-2 text-left">Ticker</th>
                  <th className="px-3 py-2 text-left">Contract</th>
                  <th className="px-3 py-2 text-right">Qty</th>
                  <th className="px-3 py-2 text-right">Entry</th>
                  <th className="px-3 py-2 text-right">Last</th>
                  <th className="px-3 py-2 text-right">P/L %</th>
                  <th className="px-3 py-2 text-left">Reason</th>
                </tr>
              </thead>
              <tbody>
                {status.open_trades.map((t) => (
                  <tr key={t.id} className="border-t border-white/10 text-gray-200">
                    <td className="px-3 py-2 font-semibold text-yellow-200">{t.symbol}</td>
                    <td className="px-3 py-2 font-mono text-xs">{t.option_symbol}</td>
                    <td className="px-3 py-2 text-right">{t.qty}</td>
                    <td className="px-3 py-2 text-right">{Number(t.entry_price || 0).toFixed(2)}</td>
                    <td className="px-3 py-2 text-right">{Number(t.last_price || 0).toFixed(2)}</td>
                    <td className={`px-3 py-2 text-right ${Number(t.pnl_pct || 0) >= 0 ? "text-green-300" : "text-red-300"}`}>{Number(t.pnl_pct || 0).toFixed(1)}%</td>
                    <td className="px-3 py-2 text-xs text-gray-400">{t.entry_reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {scan?.length > 0 && (
        <div className="mt-5">
          <h3 className="mb-2 font-semibold text-gray-100">Bearish PUT Candidates</h3>
          <div className="grid gap-2 md:grid-cols-2">
            {scan.map((c) => (
              <div key={c.symbol} className="rounded-xl border border-white/10 bg-white/[0.03] p-3">
                <div className="flex items-center justify-between gap-2">
                  <div className="font-bold text-yellow-200">{c.symbol}</div>
                  <Pill tone={c.bearish ? "bad" : "neutral"}>Score {c.score}</Pill>
                </div>
                <div className="mt-2 text-xs text-gray-400">{(c.reasons || []).slice(0, 5).join(" • ")}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {result && (
        <details className="mt-5 rounded-xl border border-white/10 bg-black/30 p-3">
          <summary className="cursor-pointer text-sm font-semibold text-gray-200">Last PUT Bot Result</summary>
          <pre className="mt-3 max-h-96 overflow-auto whitespace-pre-wrap text-xs text-gray-300">{JSON.stringify(result, null, 2)}</pre>
        </details>
      )}

      {logs?.length > 0 && (
        <details className="mt-5 rounded-xl border border-white/10 bg-black/30 p-3">
          <summary className="cursor-pointer text-sm font-semibold text-gray-200">PUT Bot Logs</summary>
          <div className="mt-3 space-y-2">
            {logs.slice(0, 20).map((l) => (
              <div key={l.id} className="rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-xs text-gray-300">
                <div className="flex flex-wrap items-center gap-2">
                  <Pill tone={l.level === "error" ? "bad" : l.level === "warning" ? "gold" : "neutral"}>{l.level}</Pill>
                  <span className="text-gray-500">{l.created_at}</span>
                </div>
                <div className="mt-1">{l.message}</div>
              </div>
            ))}
          </div>
        </details>
      )}
    </section>
  );
}
